import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { openFile, byteSize, getSortState, Translate, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './content-images.reducer';

export const ContentImages = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, ITEMS_PER_PAGE, 'id'), location.search)
  );

  const contentImagesList = useAppSelector(state => state.contentImages.entities);
  const loading = useAppSelector(state => state.contentImages.loading);
  const totalItems = useAppSelector(state => state.contentImages.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      })
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="content-images-heading" data-cy="ContentImagesHeading">
        <Translate contentKey="activitiesApp.contentImages.home.title">Content Images</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="activitiesApp.contentImages.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/content-images/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="activitiesApp.contentImages.home.createLabel">Create new Content Images</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {contentImagesList && contentImagesList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="activitiesApp.contentImages.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('contentImage')}>
                  <Translate contentKey="activitiesApp.contentImages.contentImage">Content Image</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('contentImage')} />
                </th>
                <th className="hand" onClick={sort('name')}>
                  <Translate contentKey="activitiesApp.contentImages.name">Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('name')} />
                </th>
                <th className="hand" onClick={sort('imageType')}>
                  <Translate contentKey="activitiesApp.contentImages.imageType">Image Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('imageType')} />
                </th>
                <th className="hand" onClick={sort('activityType')}>
                  <Translate contentKey="activitiesApp.contentImages.activityType">Activity Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('activityType')} />
                </th>
                <th>
                  <Translate contentKey="activitiesApp.contentImages.city">City</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {contentImagesList.map((contentImages, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/content-images/${contentImages.id}`} color="link" size="sm">
                      {contentImages.id}
                    </Button>
                  </td>
                  <td>
                    {contentImages.contentImage ? (
                      <div>
                        {contentImages.contentImageContentType ? (
                          <a onClick={openFile(contentImages.contentImageContentType, contentImages.contentImage)}>
                            <img
                              src={`data:${contentImages.contentImageContentType};base64,${contentImages.contentImage}`}
                              style={{ maxHeight: '30px' }}
                            />
                            &nbsp;
                          </a>
                        ) : null}
                        <span>
                          {contentImages.contentImageContentType}, {byteSize(contentImages.contentImage)}
                        </span>
                      </div>
                    ) : null}
                  </td>
                  <td>{contentImages.name}</td>
                  <td>
                    <Translate contentKey={`activitiesApp.ContentImageType.${contentImages.imageType}`} />
                  </td>
                  <td>
                    <Translate contentKey={`activitiesApp.ActivityType.${contentImages.activityType}`} />
                  </td>
                  <td>{contentImages.city ? <Link to={`/city/${contentImages.city.id}`}>{contentImages.city.cityName}</Link> : ''}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/content-images/${contentImages.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/content-images/${contentImages.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/content-images/${contentImages.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="activitiesApp.contentImages.home.notFound">No Content Images found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={contentImagesList && contentImagesList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ContentImages;
